import { Group } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { ReactFlowProvider } from "@xyflow/react";
import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import {
  Outlet,
  Route,
  Routes,
  generatePath,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
  useResolvedPath
} from "react-router";
import { useConfiguratorComponents } from "../../hooks/useConfiguratorComponents";
import { useEnergyWizard } from "../../hooks/useEnergyWizard";
import { useSiteCategories } from "../../hooks/useSiteCategories";
import { useSiteWizard } from "../../hooks/useSiteWizard";
import { useToast } from "../../mantine/notifications/useToast";
import { ROUTES } from "../../routes";
import urls from "../../urls";
import type {
  Generator,
  Meter,
  MeteringOrMarketLocation
} from "../../utils/backend-types";
import {
  OBJECT_URL_NAMES_TO_OBJECT_NAMES,
  ObjectName,
  ObjectUrlName,
  StructureDiagramObjectName
} from "../../utils/enums";
import { resolveMeteringOrMarketLocationType } from "../../utils/resolveMeteringOrMarketLocationType";
import { ConnectionWizard } from "../ConnectionWizard/ConnectionWizard";
import { ConsumerWizard } from "../ConsumerWizard/ConsumerWizard";
import { ConsumptionShareModal } from "../ConsumptionShareModal/ConsumptionShareModal";
import { EdaVisualizationModal } from "../EdaVisualizationModal/EdaVisualizationModal";
import { GasConnectionWizard } from "../GasConnectionWizard/GasConnectionWizard";
import { UpdateAnlageStromstgModal } from "../GeneratorWizard/AnlageStromstgFlow/UpdateAnlageStromstgModal";
import { GhostNodeWizard } from "../GhostNodeWizard/GhostNodeWizard";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { MarketLocationWizard } from "../MarketLocationWizard/MarketLocationWizard";
import { MeteringLocationWizard } from "../MeteringLocationWizard/MeteringLocationWizard";
import { MeterWizard } from "../MeterWizard/MeterWizard";
import { BatteryStorageModal } from "../ObjectModals/BatteryStorageModal/BatteryStorageModal";
import { PersonWizard } from "../PersonWizard/PersonWizard";
import { ChangeProcessModal } from "../SiteTasks/ChangeProcessModal";
import { OnboardingMailPage } from "../SiteTasks/OnboardingWizard/OnboardingMailPage/OnboardingMailPage";
import type { OperatorChange } from "../SiteTasks/OperatorChanges/OperatorChanges.types";
import { SiteTasks } from "../SiteTasks/SiteTasks";
import type { TenantChange } from "../SiteTasks/TenantChanges/TenantChanges.types";
import {
  useChangeMutation,
  useChangeProcesses
} from "../SiteTasks/useChangeProcesses";
import { useShouldShowStaffView } from "../StaffViewToggle/useShouldShowStaffView";
import { WizardStackProvider } from "../WizardStack/WizardStackContext";
import { ComponentsPanel } from "./ComponentsPanel/ComponentsPanel";
import { NotAccessableModal } from "./NotAccessableModal/NotAccessableModal";
import { SiteAddress } from "./SiteAddress";
import "./StructureView.scss";
import { StructureViewButtons } from "./StructureViewButtons/StructureViewButtons";
import { StructureViewDiagramContainer } from "./StructureViewDiagramContainer/StructureViewDiagramContainer";
import { StructureViewModeContextProvider } from "./StructureViewModeContext";
import { UserAccessContext } from "./UserAccessContext";

export interface StructureViewProps {
  variantId: number;
  projectId: string;
  siteId: number;
}

function StructureView({ variantId, projectId, siteId }: StructureViewProps) {
  const { showErrorToast } = useToast();
  const [graphCoordinates, setGraphCoordinates] = useState({
    x: 0,
    y: 0
  });

  const [showNotAccessibleModal, setShowNotAccessibleModal] = useState(false);

  const { siteCategories } = useSiteCategories(projectId);
  const categoriesForSite = siteCategories?.find((site) => site.id === siteId);
  const siteIsFullFeedin = !!categoriesForSite?.is_full_feedin;
  const isPartialFeedin = categoriesForSite?.is_partial_feedin_site ?? false;

  const { data: generators } = useConfiguratorComponents<Generator>(
    ObjectName.Generator,
    siteId
  );
  const { data: consumers } = useConfiguratorComponents(
    ObjectName.Consumer,
    siteId
  );
  const { data: storages } = useConfiguratorComponents(
    ObjectName.Storage,
    siteId
  );
  const { data: gasConnections } = useConfiguratorComponents(
    ObjectName.GasConnection,
    siteId
  );
  const { data: persons } = useConfiguratorComponents(
    ObjectName.Person,
    variantId
  );
  const { data: connections } = useConfiguratorComponents(
    ObjectName.Connection,
    siteId
  );
  const {
    data: meters,
    invalidateConfiguratorComponents: invalidateMeterData
  } = useConfiguratorComponents<Meter>(ObjectName.Meter, siteId);
  const { data: ghostNodes } = useConfiguratorComponents(
    ObjectName.GhostNode,
    siteId
  );
  const {
    data: siteWizard,
    isLoading: siteWizardLoading,
    error: siteWizardError
  } = useSiteWizard(siteId, {
    refetchOnWindowFocus: true
  });

  const { energyWizard, error: energyWizardError } = useEnergyWizard(siteId, {
    refetchOnWindowFocus: true
  });

  const { data: operatorChanges } = useChangeProcesses<OperatorChange>({
    queryKey: "operatorChanges",
    queryUrl: urls.api.mieterstromProcesses.listOperatorChanges(siteId),
    siteId,
    options: {
      enabled: categoriesForSite?.is_partial_feedin_site,
      refetchOnWindowFocus: true
    }
  });
  const operatorChangesMutation = useChangeMutation<OperatorChange>(
    siteId,
    urls.api.mieterstromProcesses.updateOperatorChanges(siteId),
    "operatorChanges"
  );

  const { data: tenantChanges } = useChangeProcesses<TenantChange>({
    queryKey: "tenantChanges",
    queryUrl: urls.api.mieterstromProcesses.listTenantChanges(siteId),
    siteId,
    options: {
      enabled: categoriesForSite?.is_partial_feedin_site,
      refetchOnWindowFocus: true
    }
  });
  const tenantChangesMutation = useChangeMutation<TenantChange>(
    siteId,
    urls.api.mieterstromProcesses.updateTenantChanges(siteId),
    "tenantChanges"
  );

  const edaVisualizationModalResolvedPathV2 =
    useResolvedPath("./messkonzept-v2");
  const isEdaVisualizationV2ModalOpen = !!useMatch({
    path: edaVisualizationModalResolvedPathV2.pathname,
    end: false
  });

  const { shouldShowStaffView } = useShouldShowStaffView();
  const configurationFinished =
    siteWizard && siteWizard.enabled && siteWizard?.phases.configuration.done;
  const msbAuthorityFinished =
    siteWizard && siteWizard.enabled && siteWizard?.phases.msbauthority.done;
  const meteringConceptPhaseFinished =
    siteWizard?.phases?.meteringConcept?.done;

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      generators &&
      consumers &&
      storages &&
      persons &&
      connections &&
      meters &&
      ghostNodes &&
      gasConnections
    ) {
      queryClient.invalidateQueries({
        queryKey: ["sketch-data", { siteId }]
      });
      queryClient.invalidateQueries({
        queryKey: ["premium-sketch-data", { siteId }]
      });
      queryClient.invalidateQueries({
        queryKey: ["premium-metering-concept-sketch-data", { siteId }]
      });
    }
  }, [
    generators,
    consumers,
    storages,
    persons,
    connections,
    meters,
    ghostNodes,
    gasConnections,
    queryClient,
    siteId
  ]);

  const handleShowNotAccessibleModal = useCallback((show: boolean) => {
    setShowNotAccessibleModal(show);
  }, []);

  function toggleEdaVisualizationModal() {
    if (isEdaVisualizationV2ModalOpen) {
      navigate("./");
    } else {
      navigate("./messkonzept-v2");
    }
  }

  function closeModal() {
    navigate("./");
  }

  async function onSiteWizardUpdated() {
    await queryClient.invalidateQueries({
      queryKey: ["siteWizard", { siteId }]
    });
  }

  function onEnergyWizardUpdated() {
    queryClient.invalidateQueries({
      queryKey: ["energyWizard", { siteId }]
    });
  }

  function onCloseNotAccessable() {
    handleShowNotAccessibleModal(false);
  }

  function handleUpdateOperatorChangeProcess(operatorChange: OperatorChange) {
    return operatorChangesMutation
      .mutateAsync(operatorChange)
      .then(() => Promise.resolve())
      .catch((e) => {
        showErrorToast(
          "Die Aktualisierung dieses Prozesses konnte nicht abgeschlossen werden. Bitte versuchen Sie es später erneut."
        );
        return Promise.reject(e);
      });
  }

  function handleUpdateTenantChangeProcess(tenantChange: TenantChange) {
    return tenantChangesMutation
      .mutateAsync(tenantChange)
      .then(() => Promise.resolve())
      .catch((e) => {
        showErrorToast(
          "Die Aktualisierung dieses Prozesses konnte nicht abgeschlossen werden. Bitte versuchen Sie es später erneut."
        );
        return Promise.reject(e);
      });
  }

  const [colTasksExpanded, setColTasksExpanded] = useState(false);

  function handleSiteTasksToggle(isOpen: boolean) {
    setColTasksExpanded(isOpen);
  }

  const openModalFunction = () => toggleEdaVisualizationModal();

  const userCanCreateAndDelete: Record<string, boolean> = Object.values(
    StructureDiagramObjectName
  ).reduce<Record<string, boolean>>((dict, name) => {
    let canCreateAndDelete = shouldShowStaffView || !configurationFinished;
    if (
      name === ObjectName.MarketLocation ||
      name === ObjectName.MeteringLocation
    ) {
      canCreateAndDelete = shouldShowStaffView || !msbAuthorityFinished;
    }
    return { ...dict, [name]: canCreateAndDelete };
  }, {});

  const enableStructureViewDiagramLoad =
    !!generators &&
    !!consumers &&
    !!storages &&
    !!persons &&
    !!connections &&
    !!meters &&
    !!ghostNodes &&
    !!gasConnections;

  useEffect(() => {
    if (!location.hash || !enableStructureViewDiagramLoad) return;

    // allow to scroll down to {COMPONENTS_PANEL_ID}
    const id = location.hash.replace("#", "");
    requestAnimationFrame(() => {
      const target = document.getElementById(id);
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location, enableStructureViewDiagramLoad]);

  const configurationPageDetails = siteWizard?.phases.configuration;
  const regulatoryPageDetails = siteWizard?.phases.regulatory;
  const vollmachtMsbPageDetails = siteWizard?.phases.msbauthority;
  const vollmachtVermarkterPageDetails = siteWizard?.phases.dataloadauthority;
  const invoicesPageDetails = siteWizard?.phases.invoices;
  const meteringConceptPageDetails = siteWizard?.phases.meteringConcept;
  const meterPageDetails = energyWizard?.phases.meters;

  const error = siteWizardError || energyWizardError;
  return (
    <UserAccessContext.Provider value={userCanCreateAndDelete}>
      {error && <LoadOrError error={error} />}
      <StructureViewModeContextProvider>
        <WizardStackProvider siteId={siteId} variantId={variantId}>
          <div className="StructureView">
            <ReactFlowProvider>
              {isPartialFeedin && <SiteAddress siteId={siteId} />}
              <Group align="stretch" gap="lg" mb="md" wrap="nowrap">
                <div className="col-diagram">
                  <StructureViewDiagramContainer
                    enableStructureViewDiagramLoad={
                      enableStructureViewDiagramLoad
                    }
                    graphCoordinates={graphCoordinates}
                    handleShowNotAccessibleModal={handleShowNotAccessibleModal}
                    hasGasConnection={
                      gasConnections && gasConnections.length > 0 ? true : false
                    }
                    invalidateMeterData={invalidateMeterData}
                    isPartialFeedin={isPartialFeedin}
                    isPPA={categoriesForSite?.is_ppaaas ?? false}
                    license={categoriesForSite?.license}
                    meteringConceptPhaseFinished={meteringConceptPhaseFinished}
                    meters={meters}
                    projectId={projectId}
                    setGraphCoordinates={setGraphCoordinates}
                    siteId={siteId}
                    siteWizardLoading={siteWizardLoading}
                    variantId={variantId}
                  />
                </div>
                {siteWizard &&
                  energyWizard &&
                  (siteWizard.enabled || energyWizard.enabled) && (
                    <div
                      className={classNames("col-tasks", {
                        "col-tasks-expanded": colTasksExpanded
                      })}
                    >
                      <div className="col-tasks-inner">
                        <SiteTasks
                          energyWizard={energyWizard}
                          isUpdatingOperatorChanges={
                            operatorChangesMutation.isPending
                          }
                          isUpdatingTenantChanges={
                            tenantChangesMutation.isPending
                          }
                          operatorChanges={operatorChanges ?? []}
                          projectId={projectId}
                          siteIsFullFeedin={siteIsFullFeedin}
                          siteWizard={siteWizard}
                          tenantChanges={tenantChanges ?? []}
                          onEnergyWizardUpdated={onEnergyWizardUpdated}
                          onSiteWizardUpdated={onSiteWizardUpdated}
                          onToggle={handleSiteTasksToggle}
                          onUpdateOperatorChangeProcess={
                            handleUpdateOperatorChangeProcess
                          }
                          onUpdateTenantChangeProcess={
                            handleUpdateTenantChangeProcess
                          }
                        />
                      </div>
                    </div>
                  )}
              </Group>
              <StructureViewButtons
                isPartialFeedin={isPartialFeedin}
                openModal={openModalFunction}
                projectId={projectId}
                shouldShowStaffView={shouldShowStaffView}
              />
              <ComponentsPanel
                graphCoordinates={graphCoordinates}
                projectId={projectId}
                setShowNotAccessibleModal={handleShowNotAccessibleModal}
                siteId={siteId}
                variantId={variantId}
              />
              {showNotAccessibleModal && (
                <NotAccessableModal onClose={onCloseNotAccessable} />
              )}
              <Routes>
                <Route
                  element={
                    <EdaVisualizationModal
                      siteId={siteId}
                      onCloseModal={() => toggleEdaVisualizationModal()}
                    />
                  }
                  path="messkonzept-v2/"
                />
                <Route
                  element={
                    <ConsumptionShareModal
                      invalidateMeterData={invalidateMeterData}
                      meters={meters}
                      onCloseModal={closeModal}
                    />
                  }
                  path="verbrauchsanteile/:componentId"
                />
                <Route path=":objectUrlName">
                  <Route element={<Outlet />} index />
                  <Route path=":componentId">
                    <Route
                      element={
                        <UpdateAnlageStromstgModal
                          generators={generators}
                          isOpen
                          siteId={siteId}
                          variantId={variantId}
                        />
                      }
                      path="anlage-stromstg-aendern"
                    />
                    <Route
                      element={
                        <ComponentEditRoute
                          projectId={projectId}
                          siteId={siteId}
                          variantId={variantId}
                        />
                      }
                      index
                    />
                    <Route
                      element={
                        <ComponentEditRoute
                          projectId={projectId}
                          siteId={siteId}
                          variantId={variantId}
                        />
                      }
                      path="*"
                    />
                  </Route>
                </Route>
                {siteWizard && (
                  <>
                    <Route
                      element={
                        <OnboardingMailPage
                          details={configurationPageDetails}
                          phaseKey="configuration"
                          projectId={projectId}
                          siteId={siteId}
                          onClose={closeModal}
                        />
                      }
                      path="konfiguration"
                    />
                    <Route
                      element={
                        <OnboardingMailPage
                          details={regulatoryPageDetails}
                          phaseKey="regulatory"
                          projectId={projectId}
                          siteId={siteId}
                          onClose={closeModal}
                        />
                      }
                      path="regulatorischeDaten"
                    />
                    <Route
                      element={
                        <OnboardingMailPage
                          details={meteringConceptPageDetails}
                          phaseKey="meteringConcept"
                          projectId={projectId}
                          siteId={siteId}
                          onClose={closeModal}
                        />
                      }
                      path="messkonzeptDaten"
                    />
                    <Route
                      element={
                        <OnboardingMailPage
                          details={vollmachtMsbPageDetails}
                          phaseKey="msbauthority"
                          projectId={projectId}
                          siteId={siteId}
                          onClose={closeModal}
                        />
                      }
                      path="vollmacht-msb"
                    />
                    <Route
                      element={
                        <OnboardingMailPage
                          details={vollmachtVermarkterPageDetails}
                          phaseKey="dataloadauthority"
                          projectId={projectId}
                          siteId={siteId}
                          variantId={variantId}
                          onClose={closeModal}
                        />
                      }
                      path="vollmacht-vermarkter"
                    />
                    <Route
                      element={
                        <OnboardingMailPage
                          details={invoicesPageDetails}
                          phaseKey="invoices"
                          projectId={projectId}
                          siteId={siteId}
                          onClose={closeModal}
                        />
                      }
                      path="vertraege"
                    />
                    <Route
                      element={
                        <OnboardingMailPage
                          details={meterPageDetails}
                          phaseKey="meters"
                          projectId={projectId}
                          siteId={siteId}
                          onClose={closeModal}
                        />
                      }
                      path="historischeMessdaten"
                    />
                    <Route
                      element={
                        <ChangeProcessModal
                          changeProcesses={operatorChanges || []}
                          isOpen
                          projectId={projectId}
                          step="stepNewData"
                          onClose={closeModal}
                        />
                      }
                      path="betreiberwechsel/unternehmensdaten/:processId"
                    />
                    <Route
                      element={
                        <ChangeProcessModal
                          changeProcesses={operatorChanges || []}
                          isOpen
                          projectId={projectId}
                          step="stepOldContract"
                          onClose={closeModal}
                        />
                      }
                      path="betreiberwechsel/vertragsdaten/:processId"
                    />
                    <Route
                      element={
                        <ChangeProcessModal
                          changeProcesses={tenantChanges || []}
                          isOpen
                          projectId={projectId}
                          step="stepParticipation"
                          onClose={closeModal}
                        />
                      }
                      path="betreiberwechsel/teilnahme/:processId"
                    />
                    <Route
                      element={
                        <ChangeProcessModal
                          changeProcesses={tenantChanges || []}
                          isOpen
                          projectId={projectId}
                          step="stepParticipation"
                          onClose={closeModal}
                        />
                      }
                      path="mieterwechsel/teilnahme/:processId"
                    />
                    <Route
                      element={
                        <ChangeProcessModal
                          changeProcesses={tenantChanges || []}
                          isOpen
                          projectId={projectId}
                          step="stepNewData"
                          onClose={closeModal}
                        />
                      }
                      path="mieterwechsel/unternehmensdaten/:processId"
                    />
                    <Route
                      element={
                        <ChangeProcessModal
                          changeProcesses={tenantChanges || []}
                          isOpen
                          projectId={projectId}
                          step="stepOldContract"
                          onClose={closeModal}
                        />
                      }
                      path="mieterwechsel/vertragsdaten/:processId"
                    />
                  </>
                )}
              </Routes>
            </ReactFlowProvider>
          </div>
        </WizardStackProvider>
      </StructureViewModeContextProvider>
    </UserAccessContext.Provider>
  );
}

interface ComponentEditRouteProps {
  projectId: string;
  siteId: number;
  variantId: number;
}

function ComponentEditRoute({
  projectId,
  siteId,
  variantId
}: ComponentEditRouteProps) {
  const navigate = useNavigate();
  const { componentId, objectUrlName } = useParams();
  const parentObjectPath: string | null = objectUrlName
    ? generatePath(`${ROUTES.site.entry}/:objectUrlName`, {
        projectId,
        siteId: siteId.toString() ?? null,
        objectUrlName: objectUrlName
      })
    : null;

  const objectName =
    objectUrlName &&
    Object.values(ObjectUrlName).includes(objectUrlName as ObjectUrlName)
      ? OBJECT_URL_NAMES_TO_OBJECT_NAMES[objectUrlName as ObjectUrlName]
      : undefined;

  function handleCloseComponentEditRoute() {
    navigate(parentObjectPath ?? "../../");
  }

  if (
    objectName === ObjectName.GhostNode &&
    typeof componentId !== "undefined"
  ) {
    return (
      <GhostNodeWizard
        isOpen
        mode="edit"
        objectId={Number(componentId)}
        siteId={siteId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }

  if (objectName === ObjectName.Person && typeof componentId !== "undefined") {
    return (
      <PersonWizard
        isOpen
        mode="edit"
        objectId={Number(componentId)}
        siteId={siteId}
        variantId={variantId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }

  if (
    objectName === ObjectName.Consumer &&
    typeof componentId !== "undefined"
  ) {
    return (
      <ConsumerWizard
        isOpen
        mode="edit"
        objectId={Number(componentId)}
        siteId={siteId}
        variantId={variantId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }

  if (
    objectName === ObjectName.GasConnection &&
    typeof componentId !== "undefined"
  ) {
    return (
      <GasConnectionWizard
        isOpen
        mode="edit"
        objectId={Number(componentId)}
        siteId={siteId}
        variantId={variantId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }

  if (
    objectName === ObjectName.Connection &&
    typeof componentId !== "undefined"
  ) {
    return (
      <ConnectionWizard
        isOpen
        mode="edit"
        objectId={Number(componentId)}
        siteId={siteId}
        variantId={variantId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }

  if (
    objectName === ObjectName.MarketLocation &&
    typeof componentId !== "undefined"
  ) {
    return (
      <MarketLocationWizard
        isOpen
        mode="edit"
        objectId={Number(componentId)}
        siteId={siteId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }

  if (
    objectName === ObjectName.MeteringLocation &&
    typeof componentId !== "undefined"
  ) {
    return (
      <MeteringLocationWizard
        isOpen
        mode="edit"
        objectId={Number(componentId)}
        siteId={siteId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }

  if (objectName === ObjectName.Meter && typeof componentId !== "undefined") {
    return (
      <MeterWizard
        isOpen
        mode="edit"
        objectId={Number(componentId)}
        projectId={projectId}
        siteId={siteId}
        variantId={variantId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }

  if (
    objectName === ObjectName.MeteringOrMarketLocation &&
    typeof componentId !== "undefined"
  ) {
    return (
      <MeteringOrMarketLocationEditRoute
        componentId={Number(componentId)}
        siteId={siteId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }

  if (objectName === ObjectName.Storage && typeof componentId !== "undefined") {
    return (
      <BatteryStorageModal
        isOpen
        mode="edit"
        objectId={Number(componentId)}
        siteId={siteId}
        variantId={variantId}
        onClose={handleCloseComponentEditRoute}
      />
    );
  }
  if (objectName === ObjectName.Generator) {
    return; // "new modals" moved to ManagerStructureViewRoute.tsx
  }

  return null;
}

interface MeteringOrMarketLocationEditRouteProps {
  componentId: number;
  siteId: number;
  onClose: () => void;
}

function MeteringOrMarketLocationEditRoute({
  componentId,
  siteId,
  onClose
}: MeteringOrMarketLocationEditRouteProps) {
  const { data: meteringOrMarketLocations } =
    useConfiguratorComponents<MeteringOrMarketLocation>(
      ObjectName.MeteringOrMarketLocation,
      siteId
    );
  const location = meteringOrMarketLocations?.find(
    (location) => location.id === componentId
  );

  if (!location) {
    return null;
  }

  const locationType = resolveMeteringOrMarketLocationType(location.number);

  if (locationType === "meteringLocation") {
    return (
      <MeteringLocationWizard
        isOpen
        mode="edit"
        objectId={componentId}
        siteId={siteId}
        onClose={onClose}
      />
    );
  }

  return (
    <MarketLocationWizard
      isOpen
      mode="edit"
      objectId={componentId}
      siteId={siteId}
      onClose={onClose}
    />
  );
}

export { StructureView };
